import { RitzAPI, } from '@moderepo/mode-internal-apis';
/**
 * The types of action that can be dispatched to update this sub state.
 */
export var AuthActionType;
(function (AuthActionType) {
    AuthActionType["AUTHENTICATING"] = "authenticating";
    AuthActionType["AUTHENTICATE_SUCCESS"] = "authenticate success";
    AuthActionType["AUTHENTICATE_FAILURE"] = "authenticate failure";
    AuthActionType["SET_AGENT_LOGIN_INFO"] = "set agent login info";
    AuthActionType["UPDATE_LOGGED_IN_AGENT"] = "update logged in agent";
    AuthActionType["SET_SUDO_PROJECT_ACCESS_KEY"] = "set sudo project access key";
    AuthActionType["SET_SUDO_USER_ACCESS_KEY"] = "set sudo user access key";
    AuthActionType["LOGOUT_AGENT"] = "logout agent";
})(AuthActionType || (AuthActionType = {}));
export const authenticating = () => {
    return {
        type: AuthActionType.AUTHENTICATING,
    };
};
export const authenticateSuccess = (agent) => {
    return {
        type: AuthActionType.AUTHENTICATE_SUCCESS,
        loggedInAgent: agent,
    };
};
export const authenticateFailure = () => {
    return {
        type: AuthActionType.AUTHENTICATE_FAILURE,
    };
};
export const setLoginInfo = (loginInfo) => {
    return {
        type: AuthActionType.SET_AGENT_LOGIN_INFO,
        loginInfo,
    };
};
export const updateLoggedInAgent = (agent) => {
    return {
        type: AuthActionType.UPDATE_LOGGED_IN_AGENT,
        loggedInAgent: agent,
    };
};
/**
 * Create an action for setting the Project access key for the specified project
 */
export const setSudoProjectAccessKey = (projectId, key) => {
    return {
        type: AuthActionType.SET_SUDO_PROJECT_ACCESS_KEY,
        projectId,
        key,
    };
};
/**
 * Create an action for setting the user access key for the specified user
 */
export const setSudoUserAccessKey = (userId, key) => {
    return {
        type: AuthActionType.SET_SUDO_USER_ACCESS_KEY,
        userId,
        key,
    };
};
export const agentLogout = () => {
    return {
        type: AuthActionType.LOGOUT_AGENT,
    };
};
/** ******** AUTH actions for CS Agent ***************** */
/**
 * Authenticate the agent to make sure he is logged in by fetching the agent's profile. If he is not logged
 * in or his token expired, an error will be thrown.
 * @param agentId - number - The id of the agent whose profile is to be loaded
 */
export const authenticateAgent = (agentId) => {
    return async (dataDispatch) => {
        // flag that we are authenticating so that the UI doesn't do anything.
        dataDispatch(authenticating());
        try {
            const agent = await RitzAPI.getInstance().getAgentProfile(agentId);
            dataDispatch(authenticateSuccess(agent));
        }
        catch (error) {
            dataDispatch(authenticateFailure());
            throw error;
        }
    };
};
/**
 * Get the Project level access key to access project data
 * @param projectId - The id of the project the user belonged to
 */
export const fetchSudoProjectAccessKey = (projectId) => {
    return async (dataDispatch) => {
        const key = await RitzAPI.getInstance().generateProjectKey(projectId);
        await dataDispatch(setSudoProjectAccessKey(projectId, key));
    };
};
/**
 * Get the access key to make changes to a user home on their behalf
 * @param projectId - The id of the project the user belonged to
 * @param userId - The id of the user the agent is trying to access
 */
export const fetchSudoUserAccessKey = (projectId, userId) => {
    return async (dataDispatch) => {
        const key = await RitzAPI.getInstance().createUserKey(projectId, userId);
        await dataDispatch(setSudoUserAccessKey(userId, key));
    };
};
/**
 * Get an Agent's profile data for the given agent id.
 * @param agentId - number - The id of the agent whose profile is to be loaded
 */
export const fetchAgentProfile = (agentId) => {
    return async (dataDispatch) => {
        const agent = await RitzAPI.getInstance().getAgentProfile(agentId);
        await dataDispatch(updateLoggedInAgent(agent));
    };
};
/**
 * This is for updating the LOGGED IN agent's personal profile.
 */
export const updateAgentPersonalProfile = (agentId, { name, email, preferences }, reloadAgentProfile = true) => {
    return async (dataDispatch) => {
        await RitzAPI.getInstance().updateAgentPersonalProfile(agentId, {
            name, email, preferences,
        });
        if (reloadAgentProfile !== false) {
            await dataDispatch(fetchAgentProfile(agentId));
        }
    };
};
/**
 * Log the agent out
 */
export const logoutAgent = () => {
    return async (dataDispatch) => {
        try {
            await RitzAPI.getInstance().logout();
        }
        catch (error) {
            // If logout API call failed, it is probably because the agent is already logged out so don't need to do anything
        }
        RitzAPI.getInstance().clearXsrfGuard(); // Delete login info from CSAPI
        await dataDispatch(agentLogout());
    };
};
